import type { CalculatedFieldDefinition, ConditionalPredicate, FormSchemaV1 } from "./form-schema";


export interface LogicEvaluationResult {
  visibleFieldIds: Set<string>;
  hiddenFieldIds: Set<string>;
  requiredFieldIds: Set<string>;
  nextSectionId: string | null;
  endForm: boolean;
}

export function evaluateConditionalLogic(
  schema: FormSchemaV1,
  answers: Record<string, unknown>
): LogicEvaluationResult {
  const visible = new Set<string>();
  const hidden = new Set<string>();
  const required = new Set<string>();
  let nextSection: string | null = null;
  let endForm = false;

  const orderedRules = [...schema.logicRules].sort((a, b) => a.priority - b.priority);

  for (const rule of orderedRules) {
    if (!rule.enabled) continue;
    const matches = evaluatePredicate(rule.when, answers);
    if (!matches) continue;
    for (const action of rule.actions) {
      switch (action.type) {
        case "show_field":
          hidden.delete(action.targetId);
          visible.add(action.targetId);
          break;
        case "hide_field":
          visible.delete(action.targetId);
          hidden.add(action.targetId);
          break;
        case "require_field":
          required.add(action.targetId);
          break;
        case "show_section":
          // Sections are handled on the client by visibility toggles.
          break;
        case "hide_section":
          // Client interprets hidden section.
          break;
        case "jump_to_section":
          nextSection = action.targetId;
          break;
        case "end_form":
          endForm = true;
          break;
        case "set_field_value":
          answers[action.targetId] = action.value;
          break;
      }
    }
    if (endForm) break;
  }

  return {
    visibleFieldIds: visible,
    hiddenFieldIds: hidden,
    requiredFieldIds: required,
    nextSectionId: nextSection,
    endForm,
  };
}

export function computeCalculatedOutputs(
  schema: FormSchemaV1,
  answers: Record<string, unknown>
): Record<string, unknown> {
  const results: Record<string, unknown> = {};

  for (const field of schema.fields) {
    if (field.type !== "calculated") continue;
    const calculated = field as CalculatedFieldDefinition;
    try {
      const context = buildCalculationContext(calculated.inputFieldIds ?? [], answers);
      const value = evaluateCalculation(calculated.expression, context);
      results[field.id] = coerceCalculatedValue(value, calculated.outputType, calculated.precision);
    } catch (error) {
      results[field.id] = null;
      console.warn(`Failed to evaluate calculated field ${field.id}`, error);
    }
  }

  return results;
}

function evaluatePredicate(predicate: ConditionalPredicate, answers: Record<string, unknown>): boolean {
  if (predicate.type === "group") {
    if (!predicate.children || predicate.children.length === 0) {
      return true;
    }
    if (predicate.operator === "AND") {
      return predicate.children.every((child) => evaluatePredicate(child, answers));
    }
    return predicate.children.some((child) => evaluatePredicate(child, answers));
  }

  const value = answers[predicate.fieldId];
  const operator = predicate.operator;
  const expected = predicate.value;

  switch (operator) {
    case "equals":
      return compare(value, expected) === 0;
    case "not_equals":
      return compare(value, expected) !== 0;
    case "contains":
      return contains(value, expected);
    case "not_contains":
      return !contains(value, expected);
    case "greater_than":
      return Number(value) > Number(expected);
    case "greater_than_or_equal":
      return Number(value) >= Number(expected);
    case "less_than":
      return Number(value) < Number(expected);
    case "less_than_or_equal":
      return Number(value) <= Number(expected);
    case "is_empty":
      return value === null || value === undefined || value === "";
    case "is_not_empty":
      return !(value === null || value === undefined || value === "");
    case "starts_with":
      return typeof value === "string" && typeof expected === "string" && value.startsWith(expected);
    case "ends_with":
      return typeof value === "string" && typeof expected === "string" && value.endsWith(expected);
    case "between":
      if (typeof expected === "object" && expected) {
        const start = (expected as { start?: number }).start;
        const end = (expected as { end?: number }).end;
        const numeric = Number(value);
        return (start === undefined || numeric >= Number(start)) && (end === undefined || numeric <= Number(end));
      }
      return false;
    case "in":
      return Array.isArray(expected) ? expected.some((item) => compare(value, item) === 0) : false;
    case "not_in":
      return Array.isArray(expected) ? expected.every((item) => compare(value, item) !== 0) : true;
    default:
      return false;
  }
}

function compare(a: unknown, b: unknown): number {
  if (typeof a === "number" && typeof b === "number") {
    return a === b ? 0 : a > b ? 1 : -1;
  }
  const left = String(a ?? "");
  const right = String(b ?? "");
  if (left === right) return 0;
  return left > right ? 1 : -1;
}

function contains(value: unknown, expected: unknown): boolean {
  if (Array.isArray(value)) {
    return Array.isArray(expected)
      ? expected.every((item) => value.some((current) => compare(current, item) === 0))
      : value.some((current) => compare(current, expected) === 0);
  }
  if (typeof value === "string") {
    return expected === undefined ? false : value.includes(String(expected));
  }
  return false;
}

function buildCalculationContext(
  inputFieldIds: string[],
  answers: Record<string, unknown>
): Record<string, number | string | boolean | null> {
  const context: Record<string, number | string | boolean | null> = {};
  for (const fieldId of inputFieldIds) {
    const raw = answers[fieldId];
    if (raw === undefined || raw === null) {
      context[fieldId] = null;
      continue;
    }
    if (typeof raw === "number" || typeof raw === "boolean" || typeof raw === "string") {
      context[fieldId] = raw;
    } else {
      context[fieldId] = null;
    }
  }
  return context;
}

function evaluateCalculation(expression: string, context: Record<string, unknown>): unknown {
  const rewritten = expression.replace(/answer\(["']([\w-]+)["']\)/g, (_, fieldId: string) => {
    return `__answers["${fieldId}"]`;
  });

  const fn = new Function(
    "__answers",
    "sum",
    "avg",
    "min",
    "max",
    `"use strict"; return (${rewritten});`
  );

  return fn(
    context,
    (...values: unknown[]) => values.reduce<number>((total, current) => total + Number(current ?? 0), 0),
    (...values: unknown[]) => {
      const filtered = values.filter((value) => value !== null && value !== undefined);
      if (filtered.length === 0) return 0;
      const total = filtered.reduce<number>((sum, current) => sum + Number(current ?? 0), 0);
      return total / filtered.length;
    },
    (...values: unknown[]) => Math.min(...values.map((value) => Number(value ?? Number.POSITIVE_INFINITY))),
    (...values: unknown[]) => Math.max(...values.map((value) => Number(value ?? Number.NEGATIVE_INFINITY)))
  );
}

function coerceCalculatedValue(
  value: unknown,
  outputType: CalculatedFieldDefinition["outputType"],
  precision?: number
) {
  switch (outputType) {
    case "number": {
      const numeric = Number(value);
      if (Number.isNaN(numeric)) return null;
      if (typeof precision === "number") {
        return Number(numeric.toFixed(precision));
      }
      return numeric;
    }
    case "boolean":
      return Boolean(value);
    case "date":
      try {
        const date = new Date(String(value));
        return Number.isNaN(date.getTime()) ? null : date.toISOString();
      } catch {
        return null;
      }
    case "text":
    default:
      return value === undefined || value === null ? "" : String(value);
  }
}
